import { useState } from 'react';
import {
  Table,
  TableContainer,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from '@material-ui/core';

import { useStoreActions } from '~/store/hooks';
import { isoToFormat } from '~/utils/date';
import { extractErrorMessage } from '~/utils/error';
import { createValues } from './form';
import Button from '~/ui/components/common/Button';
import NoDataFound from '~/ui/components/reusable/NoDataFound';
import InterventionModal from '../popups/InterventionModal';
import Item from './Item';

import { ICarePlanIntervention } from '~/services/api/carePlanInterventions/types';
import { IFormValues } from '../popups/InterventionModal/form/types';
import { IPagePermissions } from '../../types';
import styles from './Interventions.module.scss';

interface IProps {
  patientId: number;
  items: ICarePlanIntervention[];
  permissions: IPagePermissions;
  onCarePlanChange: () => void;
}

const Interventions = ({ patientId, items, permissions, onCarePlanChange }: IProps) => {
  const [showCreate, setShowCreate] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  const { onCreateCarePlanIntervention } = useStoreActions(
    actions => actions.carePlanInterventions,
  );

  const { showError } = useStoreActions(actions => actions.snackbar);

  const handleCreateIntervention = async (values: IFormValues) => {
    const payload = {
      ...values,
      date: isoToFormat(values.date, 'y-MM-dd'),
    } as ICarePlanIntervention;

    try {
      setIsCreating(true);
      await onCreateCarePlanIntervention({ patientId, data: payload });
      onCarePlanChange();
      setShowCreate(false);
    } catch (e) {
      showError(extractErrorMessage(e));
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.headerWrapper}>
        <div className={styles.title}>Interventions</div>
        {permissions.canManage && (
          <Button variant="contained" color="primary" onClick={() => setShowCreate(true)}>
            + Add New
          </Button>
        )}
      </div>
      <div className={styles.tableContainer}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell width="20%">Date</TableCell>
                <TableCell>Reason</TableCell>
                {permissions.canManage && <TableCell width={120} />}
              </TableRow>
            </TableHead>
            <TableBody>
              {items.length ? (
                items.map(item => (
                  <Item
                    key={item.id}
                    item={item}
                    permissions={permissions}
                    onCarePlanChange={onCarePlanChange}
                  />
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={3}>
                    <NoDataFound />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </div>
      {showCreate && (
        <InterventionModal
          initialValues={createValues}
          isProcessing={isCreating}
          onSubmit={handleCreateIntervention}
          onClose={() => setShowCreate(false)}
        />
      )}
    </div>
  );
};

export default Interventions;
